import React, { useEffect, useState, useRef, useMemo } from "react";
import { isQrChat, hasMetaServiceWindow } from "../../utils/platformFamily";
import {
  Box,
  Typography,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Select,
  MenuItem,
  DialogActions,
  Button,
  CircularProgress,
  Skeleton,
  TextField,
  Snackbar,
  Alert,
  FormHelperText,
  Tooltip,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Close";
// Only for the Add-Labels button/dialog restyle — the rest of this large,
// mostly-legacy file still uses MUI icons throughout (not touched here).
import { Add as AddIx, CloseCircle } from "iconsax-react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  selectInboxInfoLabels,
  selectContactInfo,
  selectInboxInfoLoading,
  selectInboxInfoError,
  selectLabelPermissions,
  selectInboxRecentData,
  selectSelectedContactID,
  checkContactExistsInInboxData,
} from "../../redux/selectors";
import {
  assignLabel,
  createAndAssignLabel,
  fetchLabels,
  clearErrors,
  removeLabelFromContact,
  fetchContactById,
  setContactInfo,
  addNote,
  updateNote,
  deleteNote,
  invalidateCachedContact,
} from "../../redux/Slice/Inbox/inboxInfoSlice";
import { updateContactName } from "../../redux/Slice/Inbox/InboxMessageSlice";
import { updateContactNameInInbox } from "../../redux/Slice/Inbox/inboxRecentSlice";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import { updateContact } from "../../redux/Slice/Contacts/contactsSlice";
import {
  fetchScheduledMessages,
  cancelScheduledMessage,
} from "../../redux/Slice/Inbox/ScheduledMessageSlice";
import { fetchInboxTeamData } from "../../redux/Slice/Inbox/inboxSettingsSlice";
import { isSubscriptionBlockedError } from "../../utils/subscriptionError";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";
import CancelScheduleIcon from "@mui/icons-material/Cancel";
import LifecycleStageSelect from "../Lifecycle/LifecycleStageSelect";
import { slimScrollbarSx } from "../../utils/scrollbarStyles";
import ChatInfoShopifyOrders from "./ChatInfoShopifyOrders";
import useShopifyCustomerLinkSync from "../../hooks/useShopifyCustomerLinkSync";
import useWhatsappWebStatus from "../../hooks/useWhatsappWebStatus";
import WhatsAppWebLinkActions from "./Features/WhatsAppWebLinkActions";
import IconButtonWithTooltip from "./Shared/IconButtonWithTooltip";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../utils/tooltipStyles";
import { isConsoleVerbose } from "../../utils/consoleGate";

const MAX_NOTES_PER_CONTACT = 10;
const MAX_LABELS_PER_CONTACT = 5;

function ChatInfo({ contactId: contactIdProp, openLabelDialog, setOpenDialog, onClose, showCloseButton = false }) {
  const dispatch = useDispatch();
  // Live: show the Shopify icon/orders the moment this contact is linked to a Shopify customer.
  useShopifyCustomerLinkSync();
  const { guard } = useSubscriptionGuard();
  const labels = useSelector(selectInboxInfoLabels);
  const contactInfo = useSelector(selectContactInfo);
  // Meta's 24h reply-window rule doesn't apply to a QR-connected workspace (same
  // isQrConnectedWorkspace as TextBox.jsx/MessageCard.jsx/ChatInfoRail.jsx) -- gates the
  // Active/Inactive status coloring and hides the "Expires in" row below, both otherwise
  // driven by contactInfo.hoursRemaining, a Meta-only concept.
  const isQrOnlyWorkspace = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnectionType === "baileys");
  const isQrConnectedWorkspace = isQrOnlyWorkspace || isQrChat(contactInfo);
  // Only an official WhatsApp contact can have a WhatsApp Web link (same eligibility the server applies).
  // Not gated on isQrChat: a Meta contact in a workspace with a linked QR number can carry a connectionId. The status endpoint
  // returns nothing for a contact without a link anyway.
  const canHaveWhatsappWebLink = Boolean(contactInfo?._id) && (!contactInfo?.platform || contactInfo.platform === "whatsapp");
  const { status: whatsappWebInfo, reset: resetWhatsappWeb, resetting: resettingWhatsappWeb, revoke: revokeWhatsappWeb, revoking: revokingWhatsappWeb } = useWhatsappWebStatus(contactInfo?._id, canHaveWhatsappWebLink);
  const isLoading = useSelector(selectInboxInfoLoading);
  const isError = useSelector(selectInboxInfoError);
  const errorMessage = useSelector(selectInboxInfoError);
  const inboxData = useSelector(selectInboxRecentData);
  const selectedContactID = useSelector(selectSelectedContactID);
  
  // Add permission selectors
  const LabelPermissions = useSelector(selectLabelPermissions);
  const ContactPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Contact || {}
  );
  const NotesPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Notes || {}
  );
  
  // Get team members from Redux
  const teamMembers = useSelector((state) => {
    const membersData = state.inboxSettings.teamMembers;
    return Array.isArray(membersData) ? membersData : [];
  });
  
  // Prefer explicit prop or Redux selection only — do not fall back to contactInfo._id after tab/delete clears
  // selection, or stale contactInfo keeps fetchContactById looping for a removed contact.
  const contactId = contactIdProp || selectedContactID || null;
  
  // Check if the contact exists in inbox data (use the actual contactId being used)
  const contactExists = React.useMemo(() => {
    return contactId ? checkContactExistsInInboxData(inboxData, contactId) : false;
  }, [inboxData, contactId]);
  
  // Extract LabelPermissions.View to a stable value
  const hasLabelViewPermission = useMemo(() => {
    return !!LabelPermissions?.View;
  }, [LabelPermissions?.View]);

  // Extract Contact.ViewPersonalIdentificationInfo to control PII visibility
  const canViewPersonalIdentificationInfo = useMemo(() => {
    return !!ContactPermissions?.ViewPersonalIdentificationInfo;
  }, [ContactPermissions?.ViewPersonalIdentificationInfo]);
  
  // Debug logging - only log when contactId actually changes to reduce console noise
  const prevLoggedContactIdRef = useRef(null);
  useEffect(() => {
    if (prevLoggedContactIdRef.current !== contactId) {
      console.log('🎯 ChatInfo component rendered', { 
        contactIdProp, 
        contactIdFromInfo: contactInfo?._id,
        selectedContactID,
        finalContactId: contactId,
        hasContactId: !!contactId 
      });
      prevLoggedContactIdRef.current = contactId;
    }
  }, [contactId, contactIdProp, contactInfo?._id, selectedContactID]);

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [labelError, setLabelError] = useState("");

  // Set default expanded state for accordions - all closed by default
  const [conversationExpanded, setConversationExpanded] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [labelsExpanded, setLabelsExpanded] = useState(false);
  const [scheduledMessagesExpanded, setScheduledMessagesExpanded] = useState(false);

  // Scheduled messages state - use shallowEqual to prevent re-renders when array reference changes but content is same
  const scheduledMessages = useSelector(
    (state) => state.scheduledMessages?.scheduledMessages || [],
    shallowEqual
  );
  const scheduledMessagesLoading = useSelector(
    (state) => state.scheduledMessages?.loading || false
  );
  const scheduledMessagesError = useSelector(
    (state) => state.scheduledMessages?.error || null
  );

  // Add snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Add label delete handler
  const [removingLabel, setRemovingLabel] = useState("");

  // Track different loading states separately
  const [isContactLoading, setIsContactLoading] = useState(false);
  const [isLabelOperationLoading, setIsLabelOperationLoading] = useState(false);
  const [isSavingLabel, setIsSavingLabel] = useState(false);

  // Edit contact name state
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Edit contact business name state
  const [isEditingBusinessName, setIsEditingBusinessName] = useState(false);
  const [businessNameInput, setBusinessNameInput] = useState("");
  const [savingBusinessName, setSavingBusinessName] = useState(false);

  // Edit contact email state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  


  // Notes state
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [isNoteOperationLoading, setIsNoteOperationLoading] = useState(false);

  // Cancel confirmation dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [messageToCancel, setMessageToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Always Assigned state
  const [alwaysAssignedExpanded, setAlwaysAssignedExpanded] = useState(false);
  const [alwaysAssignedEnabled, setAlwaysAssignedEnabled] = useState(false);
  const [, setAlwaysAssignedTo] = useState(null);
  const [savingAlwaysAssigned, setSavingAlwaysAssigned] = useState(false);

  // Add refs to track previous values and prevent infinite loops
  const contactInfoRef = useRef(contactInfo);
  const prevContactIdRef = useRef(null);
  const labelsFetchedRef = useRef(false);

  // Fetch labels once when component mounts and has permission
  useEffect(() => {
    if (hasLabelViewPermission && !labelsFetchedRef.current) {
      labelsFetchedRef.current = true;
      dispatch(fetchLabels());
    }
  }, [dispatch, hasLabelViewPermission]);

  useEffect(() => {
    // Skip if contactId hasn't actually changed
    if (prevContactIdRef.current === contactId) {
      return;
    }
    prevContactIdRef.current = contactId;

    if (!contactId) {
      return;
    }

    // Skip re-fetch + skeleton if selectContact (useContactSelection) already loaded
    // this contact's info. This happens when setselectedContactID fires post-fetch and
    // changes contactId here — the data is already in Redux, no skeleton needed.
    const infoId = contactInfo?._id || contactInfo?.id;
    if (infoId && String(infoId) === String(contactId)) {
      return;
    }

    setIsContactLoading(true);
    dispatch(fetchContactById(contactId)).finally(() =>
      setIsContactLoading(false)
    );
  }, [dispatch, contactId, contactInfo]);

  // Update contactInfo ref whenever contactInfo changes
  useEffect(() => {
    contactInfoRef.current = contactInfo;
  }, [contactInfo]);

  // Listen to socket events and Redux state for real-time updates
  useEffect(() => {
    if (!contactId) return;

    // Listen for assignment/unassignment/close events from socket
    const handleSocketUpdate = (event) => {
      const data = event.detail || event;
      
      // CRITICAL: Check if this is an internal mention - skip assignment updates for internal mentions
      const isInternalMention = data.status === 'internal' || 
                                data.message?.status === 'internal' ||
                                data.message?.isInternalMention === true ||
                                data.isInternalMention === true;
      
      // Check if this update is for the current contact
      if (data.contactId === contactId || data.contactID === contactId) {
        // CRITICAL: Skip assignment updates for internal mentions to prevent overwriting existing assignment
        if (isInternalMention) {
          console.log("🚫 ChatInfo: Skipping assignment update for internal mention:", {
            contactId: data.contactId || data.contactID,
            status: data.status,
            messageStatus: data.message?.status
          });
          return;
        }
        
        // Get current contactInfo from ref (always has latest value)
        const currentContactInfo = contactInfoRef.current;
        
        // Update contact info in Redux when assignment/unassignment/close happens
        // IMPORTANT: Update even if only assignment fields change (not just status)
        const updatePayload = {
          _id: contactId,
          status: data.status || data.closeStatus || currentContactInfo?.status,
          assignedTo: data.assignedTo !== undefined ? data.assignedTo : (currentContactInfo?.assignedTo || null),
          assignedToName: data.assignedToName !== undefined ? data.assignedToName : (currentContactInfo?.assignedToName || null),
          assignedToId: data.assignedToId !== undefined ? data.assignedToId : (currentContactInfo?.assignedToId || null),
          // CRITICAL: Preserve alwaysAssigned fields
          alwaysAssignedEnabled: currentContactInfo?.alwaysAssignedEnabled || false,
          alwaysAssignedTo: currentContactInfo?.alwaysAssignedTo || null,
          alwaysAssignedToType: currentContactInfo?.alwaysAssignedToType || null,
        };

        // Update if there's a status change OR assignment field changes
        const hasStatusChange = data.status || data.closeStatus;
        const hasAssignmentChange = 
          (data.assignedTo !== undefined && data.assignedTo !== currentContactInfo?.assignedTo) ||
          (data.assignedToName !== undefined && data.assignedToName !== currentContactInfo?.assignedToName) ||
          (data.assignedToId !== undefined && data.assignedToId !== currentContactInfo?.assignedToId);
        
        if (hasStatusChange || hasAssignmentChange) {
          console.log("🔄 ChatInfo: Updating contact info from socket:", updatePayload);
          dispatch({ type: 'inboxInfo/setContactInfo', payload: updatePayload });
        }
      }
    };

    // Listen for assignment messages
    window.addEventListener('assignmentUpdate', handleSocketUpdate);
    // Listen for close messages
    window.addEventListener('closeUpdate', handleSocketUpdate);
    // Also listen to the custom socket update event
    window.addEventListener('contactStatusUpdate', handleSocketUpdate);

    return () => {
      window.removeEventListener('assignmentUpdate', handleSocketUpdate);
      window.removeEventListener('closeUpdate', handleSocketUpdate);
      window.removeEventListener('contactStatusUpdate', handleSocketUpdate);
    };
  }, [contactId, dispatch]); // Removed contactInfo dependencies to prevent infinite loop

  // Also watch for changes in inbox data to update contact info from Redux state
  useEffect(() => {
    if (!contactId || !inboxData) return;

    // Search all categories for this contact
    let foundContact = null;
    Object.keys(inboxData).forEach(category => {
      if (Array.isArray(inboxData[category])) {
        const contact = inboxData[category].find(c => 
          (c.contactId === contactId || c.contactID === contactId)
        );
        if (contact && (!foundContact || 
          (contact.lastMessage?.timestamp && 
           (!foundContact.lastMessage?.timestamp || 
            new Date(contact.lastMessage.timestamp) > new Date(foundContact.lastMessage.timestamp))))) {
          foundContact = contact;
        }
      }
    });

    // Get current contactInfo from ref (always has latest value)
    const currentContactInfo = contactInfoRef.current;
    
    // Only update if we found a contact AND there are actual differences to prevent infinite loops
    if (foundContact) {
      // Create a stable key for the found contact's relevant fields
      const foundContactKey = `${foundContact.status || ''}_${foundContact.assignedTo || ''}_${foundContact.assignedToName || ''}`;
      const currentContactKey = `${currentContactInfo?.status || ''}_${currentContactInfo?.assignedTo || ''}_${currentContactInfo?.assignedToName || ''}`;
      
      // Only update if the keys are different (meaning there are actual changes)
      if (foundContactKey !== currentContactKey) {
        const updatePayload = {
          _id: contactId,
          status: foundContact.status || currentContactInfo?.status,
          assignedTo: foundContact.assignedTo || null,
          assignedToName: foundContact.assignedToName || null,
        };
        dispatch({ type: 'inboxInfo/setContactInfo', payload: updatePayload });
      }
    }
  }, [contactId, dispatch, inboxData]); // Removed contactInfo dependencies to prevent infinite loop

  // Watch isLoading from Redux to track label operations
  useEffect(() => {
    setIsLabelOperationLoading(isLoading);
  }, [isLoading]);

  // Show error messages from Redux state in snackbar
  useEffect(() => {
    if (isError && errorMessage) {
      if (!isSubscriptionBlockedError(errorMessage)) {
        setSnackbar({
          open: true,
          message: errorMessage.error || errorMessage,
          severity: "error",
        });
      }
      // Always clear the error state, even if we skipped the snackbar
      dispatch(clearErrors());
    }
  }, [isError, errorMessage, dispatch]);

  // Use openLabelDialog to control dialog open state
  useEffect(() => {
    if (openLabelDialog) {
      setDialogOpen(true);
    }
  }, [openLabelDialog]);
  

  const handleOpenDialog = () => {
    // Only open dialog if user has permission to add labels
    if (LabelPermissions.Add) {
      setDialogOpen(true);
    }
  };

  // Add-Labels dropdown's row style — matches AllTemplates.jsx/
  // InboxSettings.jsx's rounded-pill/inset hover convention.
  const menuItemSx = (theme) => ({
    fontSize: "0.9375rem",
    borderRadius: "8px",
    mx: 0.5,
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    },
    "&.Mui-selected:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)",
    },
  });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLabel("");
    setNewLabel("");
    setLabelError("");
    if (setOpenDialog) setOpenDialog(null);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handle new label input change
  const handleNewLabelChange = (e) => {
    const value = e.target.value;

    // Clear selected label when typing in new label field
    if (value && selectedLabel) {
      setSelectedLabel("");
    }

    // Validate label length
    if (value.length > 10) {
      setLabelError("Label cannot exceed 10 characters");
    } else {
      setLabelError("");
    }

    setNewLabel(value);
  };

  // Handle selection from dropdown
  const handleSelectLabel = (e) => {
    const value = e.target.value;

    // Clear new label input when selecting from dropdown
    if (value && newLabel) {
      setNewLabel("");
      setLabelError("");
    }

    setSelectedLabel(value);
  };

  // Handle Enter key press in the text field
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (newLabel || selectedLabel) && !labelError) {
      e.preventDefault();
      handleSaveLabel();
    }
  };

  const handleSaveLabel = async () => {
    if (!contactInfo || !contactInfo._id) return;
    if (!await guard([])) return;

    // Don't proceed if there's a validation error
    if (labelError) return;
    
    if (contactLabels.length >= MAX_LABELS_PER_CONTACT) {
      setSnackbar({
        open: true,
        message: `Maximum ${MAX_LABELS_PER_CONTACT} labels allowed per contact`,
        severity: "warning",
      });
      return;
    }

    setIsLabelOperationLoading(true);
    setIsSavingLabel(true);
    try {
      let result;

      if (newLabel) {
        result = await dispatch(
          createAndAssignLabel({
            labelName: newLabel,
            contactId: contactInfo._id,
          })
        ).unwrap();
      } else if (selectedLabel) {
        result = await dispatch(
          assignLabel({
            labelName: selectedLabel,
            contactId: contactInfo._id,
          })
        ).unwrap();
      }

      // Show success message
      setSnackbar({
        open: true,
        message: result?.message || "Label assigned successfully",
        severity: "success",
      });

      // Close the dialog
      handleCloseDialog();

      // Ensure labels accordion stays expanded
      setLabelsExpanded(true);
    } catch (error) {
      // Error handling happens in the useEffect that watches for isError
      handleCloseDialog();
    } finally {
      setIsLabelOperationLoading(false);
      setIsSavingLabel(false);
    }
  };

  // Handle accordion changes
  const handleConversationChange = (event, isExpanded) => {
    setConversationExpanded(isExpanded);
  };

  const handleNotesChange = (event, isExpanded) => {
    setNotesExpanded(isExpanded);
  };

  const handleAlwaysAssignedChange = (event, isExpanded) => {
    setAlwaysAssignedExpanded(isExpanded);
  };

  // Check if contact is assigned (to either team member or team)
  const isAssigned = useMemo(() => {
    if (!contactInfo) return false;
    // Contact must be Assigned status
    if (contactInfo.status !== "Assigned") return false;
    // Must have assignedTo
    if (!contactInfo.assignedTo) return false;
    return true;
  }, [contactInfo]);

  // Fetch team members when component mounts
  useEffect(() => {
    if (teamMembers.length === 0) {
      dispatch(fetchInboxTeamData());
    }
  }, [dispatch, teamMembers.length]);

  // Update always assigned state when contactInfo changes
  useEffect(() => {
    if (contactInfo?._id) {
      setAlwaysAssignedEnabled(contactInfo.alwaysAssignedEnabled || false);
      setAlwaysAssignedTo(contactInfo.alwaysAssignedTo || null);
    }
  }, [contactInfo]);

  const handleLabelsChange = (event, isExpanded) => {
    setLabelsExpanded(isExpanded);
  };

  const handleScheduledMessagesChange = (event, isExpanded) => {
    setScheduledMessagesExpanded(isExpanded);
    
    // Fetch scheduled messages only when accordion is opened
    if (isExpanded && contactId) {
      console.log('📡 Fetching scheduled messages (accordion opened) for contactId:', contactId);
      dispatch(fetchScheduledMessages(contactId))
        .then((result) => {
          console.log('📥 fetchScheduledMessages result:', result);
          if (result.type === 'scheduledMessages/fetchScheduledMessages/fulfilled') {
            console.log('✅ Scheduled messages fetched successfully:', result.payload);
            console.log('✅ Number of messages:', Array.isArray(result.payload) ? result.payload.length : 'not an array');
          } else if (result.type === 'scheduledMessages/fetchScheduledMessages/rejected') {
            console.error('❌ Failed to fetch scheduled messages:', result.payload);
          }
        })
        .catch((error) => {
          console.error('❌ Error fetching scheduled messages:', error);
        });
    }
  };
   
  // Handle cancel scheduled message - show confirmation dialog
  const handleCancelClick = (message) => {
    setMessageToCancel(message);
    setCancelDialogOpen(true);
  };

  // Handle confirmed cancellation
  const handleConfirmCancel = async () => {
    if (!messageToCancel || !messageToCancel._id) return;
    if (!await guard([])) return;

    setCancelling(true);
    try {
      await dispatch(cancelScheduledMessage(messageToCancel._id)).unwrap();
      setSnackbar({
        open: true,
        message: "Scheduled message cancelled successfully",
        severity: "success",
      });
      // Refresh scheduled messages
      if (contactId) {
        dispatch(fetchScheduledMessages(contactId));
      }
      setCancelDialogOpen(false);
      setMessageToCancel(null);
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        setSnackbar({
          open: true,
          message: error || "Failed to cancel scheduled message",
          severity: "error",
        });
      }
    } finally {
      setCancelling(false);
    }
  };

  // Handle cancel dialog close
  const handleCancelDialogClose = () => {
    if (!cancelling) {
      setCancelDialogOpen(false);
      setMessageToCancel(null);
    }
  };

  // Format scheduled time
  const formatScheduledTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  // Get message preview based on type
  const getMessagePreview = (message) => {
    if (message.messageType === "text") {
      return message.messageContent || "No content";
    } else if (message.messageType === "template") {
      // Handle both object and nested object formats
      const templateName = 
        message.templateData?.name || 
        message.templateData?.template?.name || 
        "Template message";
      return templateName;
    } else if (message.mediaFileName) {
      return `${message.messageType.charAt(0).toUpperCase() + message.messageType.slice(1)}: ${message.mediaFileName}`;
    } else {
      return `${message.messageType.charAt(0).toUpperCase() + message.messageType.slice(1)} message`;
    }
  };

  // Filter scheduled messages using useMemo to prevent recalculation on every render
  // Note: API already filters by contact, but we verify as safety check
  const allScheduledMessagesForContact = useMemo(() => {
    return scheduledMessages.filter((msg) => {
      // Handle both populated contact object and contact ID string
      const msgContactId = 
        msg.contact?._id?.toString() || 
        msg.contact?.toString() || 
        msg.contact;
      const currentContactId = contactId?.toString() || contactId;
      
      // API already filters by contact, but verify as safety check
      return !contactId || msgContactId === currentContactId;
    });
  }, [scheduledMessages, contactId]);

  // Filter only pending scheduled messages using useMemo
  const pendingScheduledMessages = useMemo(() => {
    return allScheduledMessagesForContact.filter(
      (msg) => msg.status === "pending"
    );
  }, [allScheduledMessagesForContact]);

  // Debug logging to help diagnose issues
  useEffect(() => {
    console.log('🟡 Scheduled Messages Debug Effect triggered', {
      hasContactId: !!contactId,
      contactId,
      scheduledMessagesLength: scheduledMessages.length,
      scheduledMessagesLoading,
      scheduledMessagesError,
    });
    
    if (contactId) {
      if (isConsoleVerbose) {
      console.log("🔍 Scheduled Messages Debug:", {
        contactId,
        contactIdType: typeof contactId,
        totalMessagesInState: scheduledMessages.length,
        messagesForContact: allScheduledMessagesForContact.length,
        pendingCount: pendingScheduledMessages.length,
        loading: scheduledMessagesLoading,
        error: scheduledMessagesError,
        allMessages: scheduledMessages.map(msg => ({
          id: msg._id,
          contact: msg.contact?._id || msg.contact,
          contactType: typeof (msg.contact?._id || msg.contact),
          contactString: (msg.contact?._id?.toString() || msg.contact?.toString() || msg.contact),
          status: msg.status,
          scheduledTime: msg.scheduledTime,
          matchesContact: (msg.contact?._id?.toString() || msg.contact?.toString() || msg.contact) === (contactId?.toString() || contactId),
        }))
      });
      }
    } else {
      console.log('⚠️ No contactId in debug effect');
    }
  }, [contactId, scheduledMessages, allScheduledMessagesForContact, pendingScheduledMessages, scheduledMessagesLoading, scheduledMessagesError]);

  // Determine the status label
  // Website chat and QR-connected numbers have no 24-hour window, so "hoursRemaining === 0" never means Inactive there. Judged per chat: an
  // Instagram or Facebook chat of a QR-only workspace keeps its window (see hasMetaServiceWindow).
  const hasServiceWindow = hasMetaServiceWindow({ platform: contactInfo?.platform, isQrConnectedWorkspace });
  const statusLabel = hasServiceWindow && contactInfo?.hoursRemaining === 0 ? "Inactive" : "Active";

  // Ensure labels is an array before using map
  const labelsList = Array.isArray(labels) ? labels : [];

  // Ensure contactInfo.labels is an array
  const contactLabels = Array.isArray(contactInfo?.labels)
    ? contactInfo.labels
    : [];
  const hasReachedLabelsLimit = contactLabels.length >= MAX_LABELS_PER_CONTACT;

  // Add label delete handler
  const handleDeleteLabel = async (labelToDelete) => {
    if (!contactInfo || !contactInfo._id) return;
    if (!await guard([])) return;
    setRemovingLabel(labelToDelete);
    setIsLabelOperationLoading(true);
    try {
      await dispatch(
        removeLabelFromContact({
          labelName: labelToDelete,
          contactId: contactInfo._id,
        })
      ).unwrap();
      setSnackbar({
        open: true,
        message: `Label '${labelToDelete}' removed successfully`,
        severity: "success",
      });

      // Ensure labels accordion stays expanded
      setLabelsExpanded(true);
    } catch (err) {
      if (!isSubscriptionBlockedError(err)) {
        setSnackbar({
          open: true,
          message: err?.error || "Failed to remove label.",
          severity: "error",
        });
      }
    } finally {
      setRemovingLabel("");
      setIsLabelOperationLoading(false);
    }
  };

  // Notes handlers
  const handleAddNote = async () => {
    if (!newNoteText.trim() || !NotesPermissions.Add || !contactInfo || !contactInfo._id) return;
    if (!await guard([])) return;
    if (contactNotes.length >= MAX_NOTES_PER_CONTACT) {
      setSnackbar({
        open: true,
        message: `You can add up to ${MAX_NOTES_PER_CONTACT} notes per contact`,
        severity: "warning",
      });
      return;
    }
    setIsNoteOperationLoading(true);
    try {
      await dispatch(addNote({ contactId: contactInfo._id, text: newNoteText })).unwrap();
      setNewNoteText("");
      setSnackbar({
        open: true,
        message: "Note added successfully",
        severity: "success",
      });
      // Note: Redux state is already updated by addNote.fulfilled reducer
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        setSnackbar({
          open: true,
          message: error || "Failed to add note",
          severity: "error",
        });
      }
    } finally {
      setIsNoteOperationLoading(false);
    }
  };

  const handleUpdateNote = async (noteId, text) => {
    if (!text.trim() || !NotesPermissions.Edit || !contactInfo || !contactInfo._id) return;
    if (!await guard([])) return;
    setIsNoteOperationLoading(true);
    try {
      await dispatch(updateNote({ contactId: contactInfo._id, noteId, text })).unwrap();
      setEditingNoteId(null);
      setEditingNoteText("");
      setSnackbar({
        open: true,
        message: "Note updated successfully",
        severity: "success",
      });
      // Note: Redux state is already updated by updateNote.fulfilled reducer
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        setSnackbar({
          open: true,
          message: error || "Failed to update note",
          severity: "error",
        });
      }
    } finally {
      setIsNoteOperationLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!NotesPermissions.Delete || !contactInfo || !contactInfo._id) return;
    if (!await guard([])) return;
    setIsNoteOperationLoading(true);
    try {
      await dispatch(deleteNote({ contactId: contactInfo._id, noteId })).unwrap();
      setSnackbar({
        open: true,
        message: "Note deleted successfully",
        severity: "success",
      });
      // Note: Redux state is already updated by deleteNote.fulfilled reducer
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        setSnackbar({
          open: true,
          message: error || "Failed to delete note",
          severity: "error",
        });
      }
    } finally {
      setIsNoteOperationLoading(false);
    }
  };

  // Ensure contactInfo.notes is an array
  const contactNotes = Array.isArray(contactInfo?.notes)
    ? contactInfo.notes
    : [];
  const hasReachedNotesLimit = contactNotes.length >= MAX_NOTES_PER_CONTACT;

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSaveBusinessName = async () => {
    if (savingBusinessName) return;
    if (!await guard([])) return;
    try {
      setSavingBusinessName(true);
      const updatedBusinessName = businessNameInput.trim();
      const updatedContact = await dispatch(updateContact({ id: contactInfo._id, updateData: { businessName: updatedBusinessName } })).unwrap();
      setSnackbar({ open: true, message: "Business name updated", severity: "success" });
      setIsEditingBusinessName(false);
      // Apply the server's authoritative response directly instead of re-fetching
      // (a re-fetch here would just return the stale in-memory/IndexedDB contact cache)
      invalidateCachedContact(contactInfo._id);
      dispatch(setContactInfo(updatedContact));
    } catch (err) {
      setSnackbar({ open: true, message: err?.message || "Failed to update business name", severity: "error" });
    } finally {
      setSavingBusinessName(false);
    }
  };

  const handleSaveEmail = async () => {
    if (savingEmail) return;
    const updatedEmail = emailInput.trim();
    if (updatedEmail && !EMAIL_REGEX.test(updatedEmail)) {
      setSnackbar({ open: true, message: "Please enter a valid email address", severity: "warning" });
      return;
    }
    if (!await guard([])) return;
    try {
      setSavingEmail(true);
      const updatedContact = await dispatch(updateContact({ id: contactInfo._id, updateData: { email: updatedEmail } })).unwrap();
      setSnackbar({ open: true, message: "Email updated", severity: "success" });
      setIsEditingEmail(false);
      // Apply the server's authoritative response directly instead of re-fetching
      // (a re-fetch here would just return the stale in-memory/IndexedDB contact cache)
      invalidateCachedContact(contactInfo._id);
      dispatch(setContactInfo(updatedContact));
    } catch (err) {
      setSnackbar({ open: true, message: err?.message || "Failed to update email", severity: "error" });
    } finally {
      setSavingEmail(false);
    }
  };

  // Don't render if contact is selected but doesn't exist in inbox (deleted)
  if (contactId && !contactExists) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        bgcolor: (theme) => theme.palette.background.paper,
        height: "100%", // Use 100% of parent
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // Prevent outer container from scrolling
      }}
    >
      {/* Skeleton loading — shown whenever a contact is being fetched */}
      {isContactLoading && (
        <Box sx={{ padding: "18px 18px 0 18px", display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Header: avatar + name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Skeleton variant="circular" width={42} height={42} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </Box>
          {/* Field rows */}
          {[80, 65, 72, 55].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={`${w}%`} height={18} />
          ))}
          {/* Tag chips */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[48, 64, 56].map((w, i) => (
              <Skeleton key={i} variant="rounded" width={w} height={24} sx={{ borderRadius: "12px" }} />
            ))}
          </Box>
          {/* Accordion placeholders */}
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px" }} />
          ))}
        </Box>
      )}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Render Details if contactInfo exists */}
      {!isContactLoading && contactInfo && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "center", sm: "center", md: "center", lg: "center", xl: "center" },
              justifyContent: "space-between",
              padding: { xs: "17.6px 17.6px 0 17.6px", sm: "17.6px 17.6px 0 17.6px", md: "17.6px 17.6px 0 17.6px", lg: "17.6px 17.6px 0 17.6px", xl: "17.6px 17.6px 0 17.6px" },
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              minHeight: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "40px" },
              flexShrink: 0, // Prevent header from shrinking
            }}
          >
          <Typography
            variant="h6"
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "700",
                lineHeight: { xs: "normal", sm: "normal", md: "normal", lg: "normal", xl: "1" },
                paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
                display: { xs: "block", sm: "block", md: "block", lg: "block", xl: "flex" },
                alignItems: { xs: "normal", sm: "normal", md: "normal", lg: "normal", xl: "center" },
                height: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "40px" },
            }}
          >
            Details
          </Typography>
            {showCloseButton && onClose && (
              <IconButton
                onClick={onClose}
                sx={{
                  color: (theme) => theme.palette.text.primary,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          <Box 
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0, // Important for flex child
              // Shared formula (utils/scrollbarStyles.js) — single source of
              // truth, matches Dashboard.jsx exactly.
              ...slimScrollbarSx,
            }}
          >
          <Box display="flex" justifyContent="space-between" mb={0} p={1.5}>
            <Typography color="textSecondary">Name</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {isEditingName ? (
                <>
                  <TextField
                    size="small"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!nameInput || savingName) return;
                        if (!await guard([])) return;
                        try {
                          setSavingName(true);
                          const updatedName = nameInput.trim();
                          const updatedContact = await dispatch(updateContact({ id: contactInfo._id, updateData: { name: updatedName } })).unwrap();
                          setSnackbar({ open: true, message: "Name updated", severity: "success" });
                          setIsEditingName(false);
                          // Apply the server's authoritative response directly instead of re-fetching
                          // (a re-fetch here would just return the stale 5-min in-memory contact cache)
                          invalidateCachedContact(contactInfo._id);
                          dispatch(setContactInfo(updatedContact));
                          dispatch(updateContactName({ contactId: contactInfo._id, name: updatedName }));
                          dispatch(updateContactNameInInbox({ contactId: contactInfo._id, name: updatedName }));
                        } catch (err) {
                          setSnackbar({ open: true, message: err?.message || "Failed to update name", severity: "error" });
                        } finally {
                          setSavingName(false);
                        }
                      } else if (e.key === "Escape") {
                        setIsEditingName(false);
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': { paddingY: 0.5, maxWidth: '180px' },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (!nameInput || savingName) return;
                      if (!await guard([])) return;
                      try {
                        setSavingName(true);
                        const updatedName = nameInput.trim();
                        const updatedContact = await dispatch(updateContact({ id: contactInfo._id, updateData: { name: updatedName } })).unwrap();
                        setSnackbar({ open: true, message: "Name updated", severity: "success" });
                        setIsEditingName(false);
                        // Apply the server's authoritative response directly instead of re-fetching
                        // (a re-fetch here would just return the stale 5-min in-memory contact cache)
                        invalidateCachedContact(contactInfo._id);
                        dispatch(setContactInfo(updatedContact));
                        dispatch(updateContactName({ contactId: contactInfo._id, name: updatedName }));
                        dispatch(updateContactNameInInbox({ contactId: contactInfo._id, name: updatedName }));
                      } catch (err) {
                        setSnackbar({ open: true, message: err?.message || "Failed to update name", severity: "error" });
                      } finally {
                        setSavingName(false);
                      }
                    }}
                  >
                    <DoneIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setIsEditingName(false)}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography color="textPrimary" title={contactInfo.name || "N/A"} sx={{ maxWidth: 200, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {contactInfo.name || "N/A"}
                  </Typography>
                  <IconButtonWithTooltip
                    label="Edit name"
                    size="small"
                    onClick={() => {
                      setNameInput(contactInfo.name || "");
                      setIsEditingName(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButtonWithTooltip>
                </>
              )}
            </Box>
          </Box>
          {contactInfo.platform !== "instagram" && contactInfo.platform !== "facebook" && (
            <Box display="flex" justifyContent="space-between" mb={1} p={1.5}>
              <Typography color="textSecondary">Phone</Typography>
              <Typography color="textPrimary">
                {canViewPersonalIdentificationInfo && contactInfo.phoneNumberID ? `+${contactInfo.phoneNumberID}` : "***"}
              </Typography>
            </Box>
          )}

          {whatsappWebInfo && (
            <Box mb={1} paddingLeft={1.5} paddingRight={1} paddingTop={1} paddingBottom={1}>
              <Typography color="textSecondary" mb={0.5}>WhatsApp Webchat</Typography>
              <WhatsAppWebLinkActions
                status={whatsappWebInfo}
                onReset={resetWhatsappWeb}
                resetting={resettingWhatsappWeb}
                onRevoke={revokeWhatsappWeb}
                revoking={revokingWhatsappWeb}
              />
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mb={1} paddingLeft={1.5} paddingRight={1} paddingTop={1} paddingBottom={1}>
            <Typography color="textSecondary">Business name</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {isEditingBusinessName ? (
                <>
                  <TextField
                    size="small"
                    value={businessNameInput}
                    onChange={(e) => setBusinessNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSaveBusinessName();
                      } else if (e.key === "Escape") {
                        setIsEditingBusinessName(false);
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': { paddingY: 0.5, maxWidth: '180px' },
                    }}
                  />
                  <IconButton size="small" onClick={handleSaveBusinessName} disabled={savingBusinessName}>
                    <DoneIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setIsEditingBusinessName(false)} disabled={savingBusinessName}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography color="textPrimary" title={contactInfo.businessName || "N/A"} sx={{ maxWidth: 200, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {contactInfo.businessName || "N/A"}
                  </Typography>
                  <IconButtonWithTooltip
                    label="Edit business name"
                    size="small"
                    onClick={() => {
                      setBusinessNameInput(contactInfo.businessName || "");
                      setIsEditingBusinessName(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButtonWithTooltip>
                </>
              )}
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" mb={1} paddingLeft={1.5} paddingRight={1} paddingTop={1} paddingBottom={1}>
            <Typography color="textSecondary">Email</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {isEditingEmail ? (
                <>
                  <TextField
                    size="small"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSaveEmail();
                      } else if (e.key === "Escape") {
                        setIsEditingEmail(false);
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': { paddingY: 0.5, maxWidth: '180px' },
                    }}
                  />
                  <IconButton size="small" onClick={handleSaveEmail} disabled={savingEmail}>
                    <DoneIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setIsEditingEmail(false)} disabled={savingEmail}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography color="textPrimary" title={contactInfo.email || "N/A"} sx={{ maxWidth: 200, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {contactInfo.email || "N/A"}
                  </Typography>
                  <IconButtonWithTooltip
                    label="Edit email"
                    size="small"
                    onClick={() => {
                      setEmailInput(contactInfo.email || "");
                      setIsEditingEmail(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButtonWithTooltip>
                </>
              )}
            </Box>
          </Box>
          <Divider sx={{ my: 0 }} />

          <ChatInfoShopifyOrders
            contactId={contactId}
            shopifyCustomerId={contactInfo.shopifyCustomerId}
          />
          {/* ChatInfoShopifyOrders renders nothing for a non-Shopify contact
              (returns null) — only add this divider when it actually rendered
              something, or non-Shopify contacts get a stray empty divider
              stacked right under the one above. */}
          {contactInfo.shopifyCustomerId && <Divider sx={{ my: 0 }} />}

          <Box>
            <Accordion
              expanded={conversationExpanded}
              onChange={handleConversationChange}
              sx={{
                boxShadow: "0",
                bgcolor: (theme) => theme.palette.background.paper,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="conversation-details-content"
                id="conversation-details-header"
                sx={{
                  boxShadow: "0",
                  bgcolor: (theme) => theme.palette.background.paper,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ pd: 0 }}
                >
                  Conversation details
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ bgcolor: (theme) => theme.palette.background.paper }}
              >
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography color="textSecondary">Status</Typography>
                    <Chip
                      size="medium"
                      label={statusLabel}
                      sx={(theme) => {
                        const inactive = hasServiceWindow && contactInfo?.hoursRemaining === 0;
                        const dark = theme.palette.mode === "dark";
                        const bg = inactive
                          ? dark
                            ? theme.palette.error.dark
                            : theme.palette.error.main
                          : dark
                            ? theme.palette.success.dark
                            : theme.palette.success.main;
                        return {
                          bgcolor: bg,
                          color: theme.palette.common.white,
                          fontWeight: 600,
                          minWidth: 88,
                          height: 32,
                          "& .MuiChip-label": {
                            color: theme.palette.common.white,
                            px: 1.75,
                            fontWeight: 600,
                          },
                        };
                      }}
                    />
                  </Box>
                  {contactId && (
                    <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
                      <Typography color="textSecondary">Lifecycle Stage</Typography>
                      <LifecycleStageSelect
                        key={contactId}
                        contactId={contactId}
                        currentStageId={contactInfo?.lifecycleStageId}
                      />
                    </Box>
                  )}
                  {hasServiceWindow && (
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography color="textSecondary">Expires in</Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTimeIcon fontSize="small" />
                        <Typography>
                          {contactInfo.hoursRemaining || 0}h
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography color="textSecondary">Assignee</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {contactInfo?.status === "Closed" && (
                        <Box
                          sx={{
                            bgcolor: (theme) => theme.palette.success.main,
                            color: (theme) =>
                              theme.palette.getContrastText(
                                theme.palette.success.main
                              ),
                            textTransform: "none",
                            borderRadius: "50px",
                            padding: "6px 16px",
                            boxShadow: "none",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            width: "auto",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "default",
                          }}
                        >
                          <CheckIcon sx={{ fontSize: "1rem", mr: 1 }} />
                          Closed
                        </Box>
                      )}
                      {contactInfo?.status === "Unassigned" && (
                        <Box
                          sx={{
                            bgcolor: (theme) => theme.palette.warning.main,
                            color: (theme) =>
                              theme.palette.getContrastText(
                                theme.palette.warning.main
                              ),
                            textTransform: "none",
                            borderRadius: "50px",
                            padding: "6px 16px",
                            boxShadow: "none",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            width: "auto",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "default",
                          }}
                        >
                          Unassigned
                        </Box>
                      )}
                      {contactInfo?.status === "Assigned" && (
                        <Typography>
                          {contactInfo.assignedToName 
                            ? contactInfo.assignedToName
                            : contactInfo.assignedToTeam 
                              ? `Team ${contactInfo.assignedToTeam}`
                              : contactInfo.assignedTo 
                                ? contactInfo.assignedTo
                                : "Unknown"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Divider sx={{ my: 0 }} />

          {/* Always Assigned Accordion */}
          <Box>
            <Accordion
              expanded={alwaysAssignedExpanded}
              onChange={handleAlwaysAssignedChange}
              sx={{
                boxShadow: "0",
                bgcolor: (theme) => theme.palette.background.paper,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="always-assigned-content"
                id="always-assigned-header"
                sx={{
                  boxShadow: "0",
                  bgcolor: (theme) => theme.palette.background.paper,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ pd: 0 }}
                >
                  Sticky Agent
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ bgcolor: (theme) => theme.palette.background.paper }}
              >
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={2} p={1.5}>
                    <Typography color="textSecondary">Keep this contact with the same agent</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={alwaysAssignedEnabled}
                          onChange={async (e) => {
                            const newValue = e.target.checked;
                            if (!await guard([])) return;

                            // Validate before enabling
                            if (newValue && !isAssigned) {
                              setSnackbar({
                                open: true,
                                message: "Contact must be assigned first",
                                severity: "warning"
                              });
                              return;
                            }

                            setAlwaysAssignedEnabled(newValue);
                            try {
                              setSavingAlwaysAssigned(true);
                              const updatedContact = await dispatch(updateContact({
                                id: contactInfo._id,
                                updateData: {
                                  alwaysAssignedEnabled: newValue,
                                  // If enabling, alwaysAssignedTo will be set by backend to current assignedTo
                                  // If disabling, clear it
                                  alwaysAssignedTo: newValue ? null : null // Backend handles this
                                }
                              })).unwrap();
                              setSnackbar({
                                open: true,
                                message: newValue
                                  ? "Sticky agent enabled - contact will stay assigned to current assignee"
                                  : "Sticky agent disabled",
                                severity: "success"
                              });
                              // Apply the server's authoritative alwaysAssignedTo/-Type directly instead of
                              // re-fetching (a re-fetch here would just return the stale in-memory contact cache)
                              invalidateCachedContact(contactInfo._id);
                              dispatch(setContactInfo(updatedContact));
                            } catch (err) {
                              setAlwaysAssignedEnabled(!newValue); // Revert on error
                              setSnackbar({ 
                                open: true, 
                                message: err?.message || "Failed to update", 
                                severity: "error" 
                              });
                            } finally {
                              setSavingAlwaysAssigned(false);
                            }
                          }}
                          disabled={savingAlwaysAssigned || !isAssigned}
                          sx={{
                            color: (theme) => theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: (theme) => theme.palette.primary.main,
                            },
                            '&.Mui-disabled': {
                              color: (theme) => theme.palette.action.disabled,
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ margin: 0 }}
                    />
                  </Box>
                  
                  {!isAssigned && (
                    <Box p={1.5}>
                      <Typography 
                        variant="caption" 
                        color="textSecondary"
                        sx={{ 
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                          color: (theme) => theme.palette.warning.main,
                        }}
                      >
                        This option is only available for assigned contacts.
                      </Typography>
                    </Box>
                  )}
                  
                  {alwaysAssignedEnabled && isAssigned && (
                    <Box p={1.5}>
                      <Typography 
                        variant="caption" 
                        color="textSecondary"
                        sx={{ 
                          fontSize: "0.75rem",
                          fontStyle: "italic"
                        }}
                      >
                        This contact has a sticky agent: {contactInfo?.assignedToName || "the current assignee"}.
                        When you reassign from the header, the sticky agent will update automatically.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Divider sx={{ my: 0 }} />

          <Box>
            <Accordion
              expanded={notesExpanded}
              onChange={handleNotesChange}
              sx={{
                boxShadow: "0",
                bgcolor: (theme) => theme.palette.background.paper,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="notes-content"
                id="notes-header"
                sx={{
                  boxShadow: "0",
                  bgcolor: (theme) => theme.palette.background.paper,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ pd: 0 }}
                >
                  Notes
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ bgcolor: (theme) => theme.palette.background.paper }}
              >
                {!NotesPermissions.View ? (
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontStyle: "italic",
                      padding: "8px 16px",
                      textAlign: "center",
                      width: "100%",
                      borderRadius: "4px",
                      margin: "8px 0",
                    }}
                  >
                    You don't have permission to view notes
                  </Typography>
                ) : (
                  <>
                    {/* Add note input */}
                    {NotesPermissions.Add && (
                      <Box display="flex" gap={1} mb={2}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Add a note..."
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleAddNote();
                            }
                          }}
                          disabled={isNoteOperationLoading || hasReachedNotesLimit}
                          helperText={
                            hasReachedNotesLimit
                              ? `Maximum ${MAX_NOTES_PER_CONTACT} notes reached for this contact`
                              : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: (theme) => theme.palette.divider,
                              },
                            },
                            "& .MuiFormHelperText-root": {
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[300]
                                  : theme.palette.text.primary,
                              fontWeight: 500,
                            },
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleAddNote}
                          disabled={!newNoteText.trim() || isNoteOperationLoading || hasReachedNotesLimit}
                          sx={(theme) => ({
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.success.dark
                                : theme.palette.success.main,
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "20px",
                            padding: "4px 16px",
                            minWidth: "60px",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: theme.palette.success.dark,
                              boxShadow: "none",
                              color: theme.palette.common.white,
                            },
                            "&.Mui-disabled": {
                              bgcolor: theme.palette.action.disabledBackground,
                              color: theme.palette.action.disabled,
                            },
                          })}
                        >
                          {isNoteOperationLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Add"
                          )}
                        </Button>
                      </Box>
                    )}

                    {/* Notes list */}
                    {isNoteOperationLoading && contactNotes.length === 0 ? (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : contactNotes.length > 0 ? (
                      <Box
                        component="ul"
                        sx={{
                          pl: 0,
                          m: 0,
                          listStyle: "none",
                        }}
                      >
                        {contactNotes.map((note) => (
                          <Box
                            component="li"
                            key={note._id}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              mb: 1.5,
                              pb: 1.5,
                              borderBottom: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                              "&:last-child": {
                                borderBottom: "none",
                                mb: 0,
                                pb: 0,
                              },
                            }}
                          >
                            {/* Green bullet point */}
                            <Box
                              sx={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                bgcolor: (theme) => theme.palette.success.main,
                                mt: 0.75,
                                flexShrink: 0,
                                mr: 1,
                              }}
                            />
                            {editingNoteId === note._id ? (
                              <>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={editingNoteText}
                                  onChange={(e) => setEditingNoteText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleUpdateNote(note._id, editingNoteText);
                                    } else if (e.key === "Escape") {
                                      setEditingNoteId(null);
                                      setEditingNoteText("");
                                    }
                                  }}
                                  autoFocus
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        borderColor: (theme) => theme.palette.divider,
                                      },
                                    },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateNote(note._id, editingNoteText)}
                                  disabled={isNoteOperationLoading}
                                  sx={{
                                    color: (theme) => theme.palette.success.main,
                                  }}
                                >
                                  <DoneIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingNoteId(null);
                                    setEditingNoteText("");
                                  }}
                                  disabled={isNoteOperationLoading}
                                  sx={{
                                    color: (theme) => theme.palette.text.secondary,
                                  }}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <Typography
                                  sx={{
                                    flex: 1,
                                    color: (theme) => theme.palette.text.primary,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {note.text}
                                </Typography>
                                <Box display="flex" gap={0.5}>
                                  {NotesPermissions.Edit && (
                                    <IconButtonWithTooltip
                                      label="Edit note"
                                      size="small"
                                      onClick={() => {
                                        setEditingNoteId(note._id);
                                        setEditingNoteText(note.text);
                                      }}
                                      disabled={isNoteOperationLoading}
                                      sx={{
                                        color: (theme) => theme.palette.text.secondary,
                                        "&:hover": {
                                          color: (theme) => theme.palette.primary.main,
                                          bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                              ? "rgba(255, 255, 255, 0.08)"
                                              : "rgba(0, 0, 0, 0.04)",
                                        },
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButtonWithTooltip>
                                  )}
                                  {NotesPermissions.Delete && (
                                    <IconButtonWithTooltip
                                      label="Delete note"
                                      size="small"
                                      onClick={() => handleDeleteNote(note._id)}
                                      disabled={isNoteOperationLoading}
                                      sx={{
                                        color: (theme) => theme.palette.text.secondary,
                                        "&:hover": {
                                          color: (theme) => theme.palette.error.main,
                                          bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                              ? "rgba(255, 255, 255, 0.08)"
                                              : "rgba(0, 0, 0, 0.04)",
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButtonWithTooltip>
                                  )}
                                </Box>
                              </>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        color="textSecondary"
                        sx={{
                          textAlign: "center",
                          padding: "16px",
                          fontStyle: "italic",
                        }}
                      >
                        No notes yet
                      </Typography>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
          <Divider sx={{ my: 0 }} />

          {/* Contact labels Accordion */}
          <Box>
            <Accordion
              expanded={labelsExpanded}
              onChange={handleLabelsChange}
              sx={{
                boxShadow: "0",
                bgcolor: (theme) => theme.palette.background.paper,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="contact-labels-content"
                id="contact-labels-header"
                sx={{
                  boxShadow: "0",
                  bgcolor: (theme) => theme.palette.background.paper,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Contact labels
                  </Typography>
                </Box>
                
              </AccordionSummary>
              <AccordionDetails
                sx={{ bgcolor: (theme) => theme.palette.background.paper }}
              >
                {!LabelPermissions.View ? (
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontStyle: "italic",
                      padding: "8px 16px",
                      textAlign: "center",
                      width: "100%",
                      borderRadius: "4px",
                      margin: "8px 0",
                    }}
                  >
                    You don't have permission to view labels
                  </Typography>
                ) : contactLabels.length > 0 ? (
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    alignItems="center"
                  >
                    {isLabelOperationLoading && (
                      <CircularProgress size={22} sx={{ ml: 1, mr: 1 }} />
                    )}
                    {contactLabels.map((label, index) => (
                      <Chip
                        key={index}
                        label={label}
                        sx={{
                          bgcolor: (theme) => theme.palette.background.default,
                          color: (theme) => theme.palette.text.primary,
                        }}
                        onDelete={
                          LabelPermissions.Delete
                            ? () => handleDeleteLabel(label)
                            : undefined
                        }
                        deleteIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                        disabled={
                          removingLabel === label || isLabelOperationLoading
                        }
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="textSecondary">
                    No labels assigned
                  </Typography>
                )}
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Tooltip
                    title={
                      !LabelPermissions.Add
                        ? "You don't have permission to add labels"
                        : ""
                    }
                    arrow
                    componentsProps={DARK_BUBBLE_TOOLTIP_SX}
                  >
                    <span>
                      {/* Was a filled green pill (borderRadius:20px) with a
                          plain "+" text glyph — restyled to this session's
                          plain-outlined/squared-corner/Iconsax convention
                          (Rahim, 2026-08-24: "fix the ui as well to match the
                          new one"). */}
                      <Button
                        variant="outlined"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.8125rem",
                          textTransform: "none",
                          borderRadius: "8px",
                          padding: "4px 12px",
                          minWidth: "40px",
                        }}
                        startIcon={<AddIx size="16" variant="Linear" color="currentColor" />}
                        onClick={handleOpenDialog}
                        disabled={!LabelPermissions.Add}
                      >
                        Add
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Dialog
              open={isDialogOpen}
              onClose={handleCloseDialog}
              fullWidth
              maxWidth="xs"
              PaperProps={{
                sx: {
                  // Was 16px — matches the 8-12px radius convention every
                  // other dialog this session uses.
                  borderRadius: "12px",
                  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
                  padding: 0,
                  backgroundColor: (theme) => theme.palette.background.paper,
                },
              }}
            >
              {/* Title was fontWeight:"bold" responsively bumping to 24px —
                  flattened to the Section Title token (1rem/600) matching
                  every other dialog built this session; close icon swapped
                  to Iconsax (Rahim, 2026-08-24: "fix the ui... match the new
                  one"). */}
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: 600,
                  fontSize: "1rem",
                  padding: { xs: 1.25, sm: 1.5 },
                  marginBottom: 0,
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                Add Labels
                <IconButton
                  onClick={handleCloseDialog}
                  size="small"
                  aria-label="Close add labels dialog"
                >
                  <CloseCircle size="20" variant="Linear" color="currentColor" />
                </IconButton>
              </DialogTitle>
              <DialogContent
                dividers={false}
                sx={{
                  px: { xs: 2, sm: 2.5, md: 3 },
                  pb: { xs: 1.5, sm: 2 },
                  flex: "0 1 auto",
                  overflowY: "visible",
                  backgroundColor: (theme) => theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    pt: { xs: 2, sm: 2.25, md: 2.5 },
                  }}
                >
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    {/* Rounded-pill/inset row treatment, same convention as
                        every other list this session (AllTemplates.jsx,
                        InboxSettings.jsx) — was flat/square MUI default. */}
                    <Select
                      labelId="select-label"
                      value={selectedLabel}
                      onChange={handleSelectLabel}
                      displayEmpty
                      disabled={!!newLabel}
                      // The dropdown's own popover was still plain MUI
                      // default (square corners, flat hover) even after the
                      // trigger field was restyled (Rahim, 2026-08-24, twice:
                      // "drop down is still the old one").
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            borderRadius: "10px",
                            boxShadow: (theme) =>
                              theme.palette.mode === "dark"
                                ? "0px 4px 12px rgba(0,0,0,0.4)"
                                : "0px 4px 12px rgba(0,0,0,0.15)",
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                          },
                        },
                      }}
                      sx={{
                        borderRadius: "10px",
                        fontSize: "0.9375rem",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: (theme) => theme.palette.divider,
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={menuItemSx}>
                        Select a label
                      </MenuItem>
                      {labelsList.map((label, index) => (
                        <MenuItem key={index} value={label} sx={menuItemSx}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Was no explicit fontSize (inherits this theme's tiny
                      default) — bumped along with the Select/TextField below
                      (Rahim, 2026-08-24: "font is very small for para
                      font"). */}
                  <Typography
                    align="center"
                    sx={{ my: 2, fontSize: "0.9375rem", fontWeight: 500, color: (theme) => theme.palette.text.secondary }}
                  >
                    or
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Type to add a label"
                    variant="outlined"
                    value={newLabel}
                    onChange={handleNewLabelChange}
                    onKeyDown={handleKeyDown}
                    error={!!labelError}
                    helperText={labelError}
                    disabled={!!selectedLabel || hasReachedLabelsLimit}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontSize: "0.9375rem",
                        "& fieldset": {
                          borderColor: (theme) => theme.palette.divider,
                        },
                      },
                    }}
                    inputProps={{ maxLength: 10 }}
                  />
                  {!labelError && (
                    <FormHelperText
                      sx={{
                        textAlign: "center",
                        mt: 1,
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[300]
                            : theme.palette.text.primary,
                      }}
                    >
                      {hasReachedLabelsLimit
                        ? `Maximum ${MAX_LABELS_PER_CONTACT} labels reached for this contact`
                        : "Maximum 10 characters allowed"}
                    </FormHelperText>
                  )}
                </Box>
              </DialogContent>
              {/* Footer — Cancel was plain text (no border), Add was a
                  filled-green 24px pill at 16px font. Squared corners +
                  outlined Cancel + primary (not success-green) Add, matching
                  every other dialog footer this session. */}
              <DialogActions
                sx={{
                  padding: { xs: 1.5, sm: 2 },
                  justifyContent: "space-between",
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Was padding:"8px 24px" + minWidth:100px — noticeably
                    oversized next to every other dialog's footer buttons
                    this session (Rahim, 2026-08-24: "the button are too
                    big"). Matches TemplateSendBox's footerBtnSx sizing now. */}
                <Button
                  variant="outlined"
                  onClick={handleCloseDialog}
                  sx={{ textTransform: "none", fontSize: "0.8125rem", py: 0.75, borderRadius: "8px" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveLabel}
                  disabled={
                    (!newLabel && !selectedLabel) ||
                    !!labelError ||
                    isSavingLabel ||
                    hasReachedLabelsLimit
                  }
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    textTransform: "none",
                    py: 0.75,
                    px: 2,
                    fontSize: "0.8125rem",
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  {isSavingLabel ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Add"
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          <Divider sx={{ my: 0 }} />
          <Box>
            <Accordion
              expanded={scheduledMessagesExpanded}
              onChange={handleScheduledMessagesChange}
              sx={{
                boxShadow: "0",
                bgcolor: (theme) => theme.palette.background.paper,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="scheduled-messages-content"
                id="scheduled-messages-header"
                sx={{
                  boxShadow: "0",
                  bgcolor: (theme) => theme.palette.background.paper,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Scheduled Messages
                  </Typography>
                  {pendingScheduledMessages.length > 0 && (
                    <Chip
                      label={pendingScheduledMessages.length}
                      size="small"
                      sx={{
                        bgcolor: (theme) => theme.palette.primary.main,
                        color: "white",
                        height: "20px",
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ 
                  bgcolor: (theme) => theme.palette.background.paper,
                  padding: '16px !important',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  // Custom scrollbar styling for webkit browsers (Chrome, Safari, Edge)
                  '&::-webkit-scrollbar': {
                    width: '2px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.08)',
                    borderRadius: '5px',
                    margin: '4px 0',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.25)'
                      : 'rgba(0, 0, 0, 0.25)',
                    borderRadius: '5px',
                    border: (theme) => `2px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.08)'}`,
                    '&:hover': {
                      background: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.4)'
                        : 'rgba(0, 0, 0, 0.4)',
                    },
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: 'thin',
                  scrollbarColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25) rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.08)',
                }}
              >
                {scheduledMessagesError ? (
                  <Box p={2}>
                    <Alert severity="error" sx={{ mb: 1 }}>
                      Error loading scheduled messages: {scheduledMessagesError}
                    </Alert>
                    <Button
                      size="small"
                      onClick={() => contactId && dispatch(fetchScheduledMessages(contactId))}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : scheduledMessagesLoading ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : scheduledMessages.length === 0 ? (
                  <Typography color="textSecondary" textAlign="center" p={2}>
                    No scheduled messages found
                  </Typography>
                ) : pendingScheduledMessages.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={2} p={1}>
                    {pendingScheduledMessages.map((message) => (
                      <Box
                        key={message._id}
                        sx={{
                          p: 2,
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          bgcolor: (theme) => theme.palette.background.default,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          mb={1}
                        >
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Chip
                                label={message.messageType}
                                size="small"
                                sx={{
                                  bgcolor: (theme) => theme.palette.primary.main,
                                  color: "white",
                                  fontSize: "0.7rem",
                                  height: "20px",
                                }}
                              />
                              <Chip
                                label={message.status}
                                size="small"
                                sx={{
                                  bgcolor: (theme) => theme.palette.warning.main,
                                  color: "white",
                                  fontSize: "0.7rem",
                                  height: "20px",
                                }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              color="textPrimary"
                              sx={{ mb: 1, wordBreak: "break-word" }}
                            >
                              {getMessagePreview(message)}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTimeIcon fontSize="small" />
                              <Typography variant="caption" color="textSecondary">
                                Scheduled: {formatScheduledTime(message.scheduledTime)}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleCancelClick(message)}
                            disabled={cancelling}
                            sx={{
                              color: (theme) => theme.palette.error.main,
                              "&:hover": {
                                bgcolor: (theme) =>
                                  theme.palette.error.main + "20",
                              },
                              "&.Mui-disabled": {
                                color: (theme) => theme.palette.action.disabled,
                              },
                            }}
                            title="Cancel scheduled message"
                          >
                            <CancelScheduleIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box>
                    <Typography color="textSecondary" textAlign="center" p={2}>
                      No scheduled messages
                    </Typography>
                    {scheduledMessages.length > 0 && (
                      <Typography 
                        variant="caption" 
                        color="textSecondary" 
                        textAlign="center" 
                        sx={{ display: "block", mb: 1 }}
                      >
                        ({scheduledMessages.length} total message{scheduledMessages.length !== 1 ? 's' : ''} found, but none are pending)
                      </Typography>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
          </Box>
         

          {/* Scheduled Messages Section */}
         

          {/* Cancel Confirmation Dialog */}
          <Dialog
            open={cancelDialogOpen}
            onClose={handleCancelDialogClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
                backgroundColor: (theme) => theme.palette.background.paper,
              },
            }}
          >
            <DialogTitle
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "20px",
                paddingBottom: "16px",
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              Cancel Scheduled Message
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to cancel this scheduled message?
              </Typography>
              {messageToCancel && (
                <Box
                  sx={{
                    p: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.default,
                    mb: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      label={messageToCancel.messageType}
                      size="small"
                      sx={{
                        bgcolor: (theme) => theme.palette.primary.main,
                        color: "white",
                        fontSize: "0.7rem",
                        height: "20px",
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="textPrimary" sx={{ mb: 1 }}>
                    {getMessagePreview(messageToCancel)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="caption" color="textSecondary">
                      Scheduled: {formatScheduledTime(messageToCancel.scheduledTime)}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                padding: "0 24px 24px",
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={handleCancelDialogClose}
                disabled={cancelling}
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                Keep Scheduled
              </Button>
              <Button
                onClick={handleConfirmCancel}
                disabled={cancelling}
                variant="contained"
                color="error"
                sx={{
                  borderRadius: "24px",
                  textTransform: "none",
                  padding: "8px 24px",
                  fontSize: "16px",
                  minWidth: "100px",
                }}
              >
                {cancelling ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Cancel Message"
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

// Memoize component to prevent unnecessary re-renders from parent
// Custom comparison function that ignores function reference changes
export default React.memo(ChatInfo, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.contactId === nextProps.contactId &&
    prevProps.openLabelDialog === nextProps.openLabelDialog &&
    prevProps.showCloseButton === nextProps.showCloseButton
    // Ignore function props (setOpenDialog, onClose) as they may have new references
    // but their functionality doesn't change
  );
});
